<script>
import { mapGetters, mapActions, mapState } from 'vuex'

import capitalize from 'lodash/capitalize'
import get from 'lodash/get'
import ProviderInformation from './tabs/provider-information'
import ProviderContracts from './tabs/provider-contracts'
import ProviderCatalogue from './tabs/provider-catalogue'
import ProviderCategories from './tabs/provider-categories'
import ProviderPublishing from './tabs/provider-publishing'
import ProviderCatalogueUpdates from './tabs/provider-catalogue-updates'

export default {
  name: 'ProviderDetails',
  components: {
    ProviderInformation,
    ProviderContracts,
    ProviderCatalogue,
    ProviderCategories,
    ProviderPublishing,
    ProviderCatalogueUpdates,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activeTab: '',
      loading: true,
    }
  },
  computed: {
    ...mapState('ui', ['elasticUpdateDelay']),
    ...mapGetters('providers', ['provider']),
    ...mapGetters('me', [
      'canSearchProvider',
      'canListContract',
      'canListProviderProduct',
      'canUpdateProvider',
      'canCreateContract',
      'providerPublishInternal',
    ]),
    providerData() {
      return this.provider || {}
    },
    providerName() {
      return get(this.provider, 'name', 'Provider')
    },
    tabs() {
      const tabs = []

      tabs.push({
        label: 'Information',
        name: 'information',
      })

      if (this.canListContract) {
        tabs.push({
          label: 'Contracts',
          name: 'contracts',
        })
      }

      if (this.canListProviderProduct) {
        tabs.push({
          label: 'Provider Catalogue',
          name: 'catalogue',
        })
      }

      if (this.providerPublishInternal) {
        tabs.push(
          {
            label: 'Catalogue Updates',
            name: 'catalogue-updates',
          },
          {
            label: 'Publishing',
            name: 'publishing',
          }
        )
      }

      return tabs
    },
    commands() {
      const commands = []

      if (this.canCreateContract) {
        commands.push({
          name: 'add-contract',
          label: 'Add Contract',
        })
      }

      if (this.canUpdateProvider) {
        commands.push({
          name: 'provider-manage',
          label: 'Edit Provider Details',
        })
      }

      if (this.providerPublishInternal && this.providerData.total_contracts) {
        commands.push(
          {
            label: 'Update Catalogue from Suppliers',
            name: 'update-catalogue',
          },
        )
      }
      if (this.providerPublishInternal && this.providerData.total_products) {
        commands.push(
          {
            label: 'Publish Catalogue Externally',
            name: 'publish-catalogue',
          },
        )
      }

      return commands
    },
  },
  async created() {
    const [err] = await this.getProvider(this.id)

    if (err) {
      this.getErrorMessage(err, 'Provider', 'ID', this.id)
    }

    this.loading = false

    const { tab } = this.$route.params

    if (
      (!this.canListContract && tab === 'contracts') ||
      (!this.canListProviderProduct && tab === 'catalogue')
    ) {
      this.activeTab = 'information'
      this.$router.replace({
        name: 'provider-details',
        params: {
          id: this.id,
        },
      })
    } else {
      this.activeTab = tab || this.tabs[0].name
    }
  },
  methods: {
    ...mapActions('providers', ['getProvider']),
    ...mapActions('publishing', ['publishInternal']),
    capitalize,
    onTabClick(newTab, prevTab) {
      if (newTab !== prevTab) {
        this.$router.push({
          name: 'provider-details',
          params: {
            id: this.id,
            tab: newTab,
          },
        })
      }
    },
    onMenuSelect(name) {
      if (name === 'publish-catalogue') {
        this.performInternalPublish()
      } else {
        this.$modal.show(name, {
          props: {
            provider: this.providerData,
          },
        })
      }
    },
    performInternalPublish() {
      this.publishInternal([this.id]).then(response => {
        this.$notifier({
          response,
          successMsg: 'Successfully Published',
        }).then(success => {
          const internalId = success.internal_publishing_status_id
          if (internalId) {
            // NOTE Wait becase backend need a second for update data
            // It's temporary solution, time will be tuned
            setTimeout(() => {
              this.$router.push({
                name: 'publish-details',
                params: {
                  id: internalId,
                },
              })
            }, this.elasticUpdateDelay)
          }
        })
      })
    },
  },
}
</script>

<template>
  <main-layout
    :title="providerName"
    :back="canSearchProvider && { name: 'providers' }"
    :loading="loading"
    :error-message="errorMessageText"
  >
    <el-dropdown
      v-if="commands.length && !errorMessageText"
      slot="header"
      trigger="click"
      @command="onMenuSelect"
    >
      <dropdown-icon />
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item
          v-for="(command, index) in commands"
          :key="index"
          :command="command.name"
        >
          {{ command.label }}
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
    <el-tabs
      v-model="activeTab"
      :before-leave="onTabClick"
    >
      <el-tab-pane
        v-for="tab in tabs"
        :key="tab.name"
        :label="tab.label"
        :name="tab.name"
        lazy
      >
        <component
          :is="`Provider${capitalize(tab.name)}`"
          v-if="activeTab === tab.name"
          :loading="loading"
          :provider="providerData"
        />
      </el-tab-pane>
    </el-tabs>
  </main-layout>
</template>
